import { useEffect, useMemo, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { keyService } from '@/services';
import { AI_PROVIDERS, type AiProvider, type GetUserApiKeyResponse, validateSchemaSync, upsertUserApiKeySchema } from '@arrowise/shared';
import { useAuth } from '@/providers';
import { KeyRound, ShieldAlert, Trash2 } from 'lucide-react';
import { DeleteConfirmDialog } from '@/components/dialogs/DeleteConfirmDialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { showSupportDialog } from '@/components/support/showSupportDialog';

export function KeySettings() {
    const { getUserPrimaryTeam, isTeamOwner, user, refreshUser } = useAuth();
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [keyInfo, setKeyInfo] = useState<GetUserApiKeyResponse | null>({
        provider: user?.aiProvider || null,
        configured: !!user?.aiProvider,
        availableProviders: AI_PROVIDERS,
    });
    const [provider, setProvider] = useState<AiProvider | ''>(keyInfo!.provider || '');
    const [apiKey, setApiKey] = useState('');




    const PROVIDER_MODEL_HINT: Record<string, string> = useMemo(() => ({
        openai: 'gpt-4o-mini',
        anthropic: 'claude-3-5-sonnet-latest',
        google: 'gemini-1.5-flash',
    }), []);

    const selectableProviders = useMemo(
        () => (keyInfo?.availableProviders || []).filter((p) => p !== 'azure_openai') as AiProvider[],
        [keyInfo?.availableProviders]
    );

    // Prevent selecting or persisting azure_openai in UI
    useEffect(() => {
        if (provider === 'azure_openai') {
            setProvider('');
        }
    }, [provider]);

    const userTeam = useMemo(() => getUserPrimaryTeam(), [getUserPrimaryTeam]);
    const isOwner = userTeam ? isTeamOwner(userTeam.team.id) : false;
    const isTeamMemberNotOwner = !!userTeam && !isOwner;


    const handleSave = async () => {
        if (!provider || !apiKey) {
            toast.error('Provider and API key are required');
            return;
        }
        const validation = validateSchemaSync(upsertUserApiKeySchema as any, { provider, apiKey });
        if (!validation.success || !validation.data) {
            const message = validation.error?.errors?.[0]?.message || 'Invalid API key payload';
            toast.error(message);
            return;
        }
        setSaving(true);
        try {
            await keyService.upsertUserKey({ provider, apiKey });
            toast.success('API key saved');
            const info = await keyService.getUserKeyInfo();
            setKeyInfo(info);
            setApiKey('');
            await refreshUser();
        } catch (e: any) {
            toast.error(e.message || 'Failed to save API key');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (deleting) return;
        setDeleting(true);
        try {
            await keyService.deleteUserKey();
            toast.success('API key removed');
            const info = await keyService.getUserKeyInfo();
            setKeyInfo(info);
            setApiKey('');
            await refreshUser();
        } catch (e: any) {
            toast.error(e.message || 'Failed to delete API key');
        } finally {
            setDeleting(false);
        }
    };

    if (isTeamMemberNotOwner) {
        return (
            <Card className="p-6">
                <div className="flex items-start gap-3">
                    <div className="p-2 bg-orange-100 dark:bg-orange-950/50 rounded-full">
                        <ShieldAlert className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                    </div>
                    <div className="space-y-1">
                        <h3 className="text-lg font-medium">Access Restricted</h3>
                        <p className="text-sm text-muted-foreground">
                            Only team owners can manage the AI provider API key for the team.
                        </p>
                    </div>
                </div>
            </Card>
        );
    }

    return (
        <Card className="p-6">
            <div className="space-y-6">
                <div>
                    <h3 className="text-lg font-medium flex items-center gap-2">
                        <KeyRound className="h-5 w-5" /> AI Provider Key
                    </h3>
                    <p className="text-sm text-muted-foreground">
                        Configure your AI provider and API key. Keys are stored as secure hashes and never shown.
                    </p>
                </div>

                <Separator />

                {keyInfo && (
                    <div className="flex items-center justify-between p-4 border rounded-lg">
                        <div>
                            <h4 className="font-medium">Current Status</h4>
                            <p className="text-sm text-muted-foreground">
                                Provider: {keyInfo.provider || '—'}
                            </p>
                        </div>
                        <Badge variant={keyInfo.configured ? 'default' : 'secondary'}>
                            {keyInfo.configured ? 'Configured' : 'Not Configured'}
                        </Badge>
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <Label htmlFor="provider">Provider</Label>
                        <Select value={(provider as string) || ''} onValueChange={(v) => setProvider(v as AiProvider)}>
                            <SelectTrigger className="mt-1 cursor-pointer w-full">
                                <SelectValue placeholder="Select provider" />
                            </SelectTrigger>
                            <SelectContent>
                                {selectableProviders.map((p) => (
                                    <SelectItem key={p} value={p} textValue={p} className="cursor-pointer">
                                        {p}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {provider && PROVIDER_MODEL_HINT[provider] && (
                            <p className="text-xs text-muted-foreground mt-1">Model: {PROVIDER_MODEL_HINT[provider]}</p>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="apiKey">API Key</Label>
                        <Input
                            id="apiKey"
                            type="password"
                            placeholder="Enter API key"
                            value={apiKey}
                            onChange={(e) => setApiKey(e.target.value)}
                        />
                        <p className="text-xs text-muted-foreground">Your key is encrypted on save and never displayed.</p>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    <Button onClick={handleSave} disabled={saving || !provider || apiKey.length === 0}>
                        {saving ? 'Saving...' : 'Save Key'}
                    </Button>
                    {keyInfo?.configured && (
                        <Button variant="destructive" onClick={() => setConfirmOpen(true)} disabled={deleting} className="flex items-center gap-2">
                            <Trash2 className="h-4 w-4" />
                            Remove Key
                        </Button>
                    )}
                    <div className="ml-auto">
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        type="button"
                                        variant="secondary"
                                        onClick={() => {
                                            const subj = `Request different AI model: ${provider || 'unspecified'}`
                                            showSupportDialog(undefined, undefined, undefined, subj)
                                        }}
                                        aria-label="Request a different model"
                                    >
                                        Using a different model?
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>Request a different model</TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </div>
                </div>
                <DeleteConfirmDialog
                    open={confirmOpen}
                    onOpenChange={setConfirmOpen}
                    title="Remove API Key?"
                    description={<span>This will remove the stored API key and provider from your account.</span>}
                    confirmLabel={deleting ? 'Removing...' : 'Remove Key'}
                    onConfirm={handleDelete}
                    confirmVariant="destructive"
                />
            </div>
        </Card>
    );
}

export default KeySettings;


